import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cacheCompanyModules, resolveModuleCacheKey } from '../utils/companyModules';
import { request } from '../api/request';
import {
  isFullCompanyAccess,
  hasCompanyPermission,
  hasAnyCompanyPermission,
  hasAllCompanyPermissions,
} from '../utils/companyPermissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const loadCompanyAccess = useCallback(async (activeUser) => {
    if (!activeUser || !['company', 'user'].includes(activeUser.type)) {
      setAccess(null);
      setPermissionsLoading(false);
      return null;
    }

    if (activeUser.type === 'company') {
      const fullAccess = {
        company_id: activeUser.company_id || activeUser.id,
        is_full_access: true,
        role_ids: [],
        roles: [],
        permissions: ['*'],
      };
      setAccess(fullAccess);
      setPermissionsLoading(false);
      return fullAccess;
    }

    setPermissionsLoading(true);
    try {
      const result = await request('/api/company/access');
      const nextAccess = {
        ...result,
        permissions: Array.isArray(result.permissions) ? result.permissions : [],
        roles: Array.isArray(result.roles) ? result.roles : [],
      };
      setAccess(nextAccess);
      setUser((current) => {
        if (!current || current.id !== activeUser.id) return current;
        const nextUser = {
          ...current,
          permissions: nextAccess.permissions,
          roles: nextAccess.roles,
          access: nextAccess,
        };
        localStorage.setItem('user', JSON.stringify(nextUser));
        return nextUser;
      });
      return nextAccess;
    } catch (error) {
      // Keep login usable when permissions are loading during a transient
      // network failure; protected API calls still enforce access server-side.
      console.warn('[Auth] تعذر تحميل صلاحيات الشركة:', error.message);
      const fallback = {
        company_id: activeUser.company_id,
        is_full_access: false,
        role_ids: [],
        roles: activeUser.roles || [],
        permissions: Array.isArray(activeUser.permissions) ? activeUser.permissions : [],
      };
      setAccess(fallback);
      return fallback;
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const applyAccessStatus = useCallback((patch) => {
    setUser((current) => {
      if (!current || !patch) return current;
      const next = { ...current, ...patch };
      try { localStorage.setItem('user', JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        const cacheKey = resolveModuleCacheKey({ slug: parsed.slug, companyId: parsed.company_id });
        if (cacheKey && parsed.riders_enabled !== undefined) {
          cacheCompanyModules(cacheKey, parsed);
        }
        loadCompanyAccess(parsed);
        if (parsed.type && parsed.type !== 'system_admin') {
          request('/api/auth/session')
            .then((session) => {
              if (cancelled) return;
              applyAccessStatus({
                access_status: session.access_status,
                company_name: session.company_name || parsed.company_name,
                company_phone: session.company_phone || parsed.company_phone,
                company_logo_url: session.company_logo_url || parsed.company_logo_url,
                rider_app_logo_url: session.rider_app_logo_url || parsed.rider_app_logo_url,
                merchant_app_logo_url: session.merchant_app_logo_url || parsed.merchant_app_logo_url,
                brand_color: session.brand_color || parsed.brand_color,
              });
            })
            .catch((err) => {
              if (cancelled) return;
              if (err.status === 401 || err.body?.code === 'COMPANY_STOPPED') {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setUser(null);
                setAccess(null);
              }
            })
            .finally(() => {
              if (!cancelled) setLoading(false);
            });
          return () => { cancelled = true; };
        }
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
    return () => { cancelled = true; };
  }, [loadCompanyAccess, applyAccessStatus]);

  useEffect(() => {
    function onTrialEnded() {
      applyAccessStatus({ access_status: 'trial_ended' });
    }
    window.addEventListener('algeza:trial-ended', onTrialEnded);
    return () => window.removeEventListener('algeza:trial-ended', onTrialEnded);
  }, [applyAccessStatus]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    const cacheKey = resolveModuleCacheKey({
      slug: userData?.slug,
      companyId: userData?.company_id,
    });
    if (cacheKey && userData?.riders_enabled !== undefined) {
      cacheCompanyModules(cacheKey, userData);
    }
    setUser(userData);
    loadCompanyAccess(userData);
  }, [loadCompanyAccess]);

  const updateUser = useCallback((patch) => {
    setUser((current) => {
      if (!current || !patch) return current;
      const next = { ...current, ...patch };
      try { localStorage.setItem('user', JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setAccess(null);
    setPermissionsLoading(false);
  }, []);

  const permissions = access?.permissions || user?.permissions || [];
  const isFullAccess = isFullCompanyAccess(user, access);
  const hasPermission = useCallback((permission) => (
    hasCompanyPermission(permissions, permission, isFullAccess)
  ), [isFullAccess, permissions]);
  const hasAnyPermission = useCallback((required) => {
    return hasAnyCompanyPermission(permissions, required, isFullAccess);
  }, [isFullAccess, permissions]);
  const hasAllPermissions = useCallback((required) => {
    return hasAllCompanyPermissions(permissions, required, isFullAccess);
  }, [isFullAccess, permissions]);

  const value = {
    user,
    loading,
    login,
    updateUser,
    logout,
    access,
    permissions,
    permissionsLoading,
    refreshPermissions: () => loadCompanyAccess(user),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isFullAccess,
    isAdmin: isFullAccess,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
